import React, { useEffect, useRef, useState } from "react";
import { Bookmark, Share2, Lock, Globe, X, Send, Copy, Check, Download, Image as ImageIcon } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import AddToShelfMenu from "./AddToShelfMenu";

const REACTIONS = [
  { key: "resonates", label: "Resonates", icon: "~" },
  { key: "questions", label: "Questions it", icon: "?" },
  { key: "expands",   label: "Expands it",  icon: "*" },
];

export default function ConceptActions({ conceptId, conceptTitle }) {
  const { user } = useAuth();
  const [pin, setPin] = useState({ pinned: false, visibility: null });
  const [reactions, setReactions] = useState({ counts: { resonates: 0, questions: 0, expands: 0 }, mine: null });
  const [shareOpen, setShareOpen] = useState(false);
  const [pinMenuOpen, setPinMenuOpen] = useState(false);
  const pinRef = useRef(null);

  useEffect(() => {
    if (!conceptId) return;
    api.get(`/pins/concept/${conceptId}`).then(({ data }) => setPin(data)).catch(() => {});
    api.get(`/reactions/${conceptId}`).then(({ data }) => setReactions(data)).catch(() => {});
  }, [conceptId, user?.user_id]);

  useEffect(() => {
    const onClick = (e) => { if (pinRef.current && !pinRef.current.contains(e.target)) setPinMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const requireAuth = () => {
    if (!user) { toast.error("Sign in to do that."); return false; }
    return true;
  };

  const setPinned = async (visibility) => {
    if (!requireAuth()) return;
    try {
      await api.post("/pins", { concept_id: conceptId, visibility });
      setPin({ pinned: true, visibility });
      toast.success(visibility === "public" ? "Pinned — others may see it." : "Pinned privately.");
    } catch { toast.error("Could not pin."); }
    setPinMenuOpen(false);
  };

  const unpin = async () => {
    if (!requireAuth()) return;
    try {
      await api.delete(`/pins/${conceptId}`);
      setPin({ pinned: false, visibility: null });
      toast.success("Unpinned.");
    } catch { toast.error("Could not unpin."); }
    setPinMenuOpen(false);
  };

  const react = async (key) => {
    if (!requireAuth()) return;
    try {
      if (reactions.mine === key) {
        await api.delete(`/reactions/${conceptId}`);
        setReactions((r) => ({
          counts: { ...r.counts, [key]: Math.max(0, r.counts[key] - 1) },
          mine: null,
        }));
      } else {
        await api.post("/reactions", { concept_id: conceptId, reaction: key });
        setReactions((r) => {
          const counts = { ...r.counts };
          if (r.mine) counts[r.mine] = Math.max(0, counts[r.mine] - 1);
          counts[key] = (counts[key] || 0) + 1;
          return { counts, mine: key };
        });
      }
    } catch { toast.error("Could not record reaction."); }
  };

  return (
    <div data-testid="concept-actions" className="flex flex-wrap items-center gap-3">
      {/* Pin */}
      <div ref={pinRef} className="relative">
        <button
          data-testid="pin-btn"
          onClick={() => pin.pinned ? setPinMenuOpen((o) => !o) : setPinMenuOpen((o) => !o)}
          className={`reaction-pill ${pin.pinned ? "active" : ""}`}
        >
          <Bookmark size={12} strokeWidth={2} fill={pin.pinned ? "currentColor" : "none"} />
          {pin.pinned ? (pin.visibility === "public" ? "Pinned · public" : "Pinned · private") : "Pin"}
        </button>
        {pinMenuOpen && (
          <div data-testid="pin-menu" className="absolute z-40 mt-2 paper p-2 min-w-[200px] shadow-lg">
            <button
              data-testid="pin-private"
              onClick={() => setPinned("private")}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-secondary transition-colors ${pin.visibility === "private" ? "text-[hsl(var(--primary))]" : ""}`}
            >
              <Lock size={13} strokeWidth={1.5} />
              <div className="flex-1">
                <div className="sans text-sm">Private</div>
                <div className="text-[11px] text-muted-foreground">Only you</div>
              </div>
            </button>
            <button
              data-testid="pin-public"
              onClick={() => setPinned("public")}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-secondary transition-colors ${pin.visibility === "public" ? "text-[hsl(var(--primary))]" : ""}`}
            >
              <Globe size={13} strokeWidth={1.5} />
              <div className="flex-1">
                <div className="sans text-sm">Public</div>
                <div className="text-[11px] text-muted-foreground">Shows in recommendations</div>
              </div>
            </button>
            {pin.pinned && (
              <button
                data-testid="pin-remove"
                onClick={unpin}
                className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-secondary transition-colors text-muted-foreground"
              >
                <X size={13} strokeWidth={1.5} />
                <div className="sans text-sm">Unpin</div>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reactions */}
      {REACTIONS.map((r) => (
        <button
          key={r.key}
          data-testid={`react-${r.key}`}
          onClick={() => react(r.key)}
          className={`reaction-pill ${reactions.mine === r.key ? "active" : ""}`}
        >
          <span className="serif italic">{r.icon}</span>
          {r.label}
          {reactions.counts[r.key] > 0 && (
            <span className="text-[10px] text-muted-foreground/80">· {reactions.counts[r.key]}</span>
          )}
        </button>
      ))}

      {/* Share */}
      <button
        data-testid="share-btn"
        onClick={() => setShareOpen(true)}
        className="reaction-pill"
      >
        <Share2 size={12} strokeWidth={2} />
        Share
      </button>

      {/* Add to shelf — only when signed in */}
      {user && <AddToShelfMenu conceptId={conceptId} />}

      {shareOpen && (
        <ShareModal
          conceptId={conceptId}
          conceptTitle={conceptTitle}
          onClose={() => setShareOpen(false)}
          canSendInApp={!!user}
        />
      )}
    </div>
  );
}

function ShareModal({ conceptId, conceptTitle, onClose, canSendInApp }) {
  const [tab, setTab] = useState("link"); // 'link' | 'card' | 'inApp'
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const url = `${API_BASE}/share/concept/${conceptId}`;
  const cardUrl = `${API_BASE}/concepts/${conceptId}/card.png`;

  const copyLink = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch { /* fallthrough */ }
    if (!ok) {
      // Fallback for environments where the Clipboard API is blocked.
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      ta.remove();
    }
    setCopied(ok);
    if (ok) {
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Could not copy — select the link manually.");
    }
  };

  useEffect(() => {
    if (tab !== "inApp") return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(q)}`)
        .then(({ data }) => setResults(data || []))
        .catch(() => setResults([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, tab]);

  const send = async () => {
    if (!picked) { toast.error("Pick a recipient."); return; }
    setSending(true);
    try {
      await api.post("/share", { concept_id: conceptId, to_email: picked.email, message });
      toast.success(`Sent to ${picked.name || picked.email}.`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send.");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/30 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="share-modal"
        className="paper p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="label">Share this correlation</div>
          <button data-testid="share-close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={16} />
          </button>
        </div>
        <h3 className="serif text-2xl text-foreground leading-tight mb-6">{conceptTitle}</h3>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            data-testid="share-tab-link"
            onClick={() => setTab("link")}
            className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border ${tab === "link" ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground"}`}
          >
            Public link
          </button>
          <button
            data-testid="share-tab-card"
            onClick={() => setTab("card")}
            className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border ${tab === "card" ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground"}`}
          >
            Download card
          </button>
          {canSendInApp && (
            <button
              data-testid="share-tab-inapp"
              onClick={() => setTab("inApp")}
              className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border ${tab === "inApp" ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground"}`}
            >
              Send to a seeker
            </button>
          )}
        </div>

        {tab === "link" && (
          <div>
            <div className="flex items-center gap-2">
              <input
                data-testid="share-link-input"
                readOnly
                value={url}
                className="input-line w-full text-[13px]"
                onFocus={(e) => e.target.select()}
              />
              <button data-testid="share-copy" onClick={copyLink} className="btn-gold flex items-center gap-2">
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-4">
              Anyone with this link can read the correlation. Login is only required to pin or react.
            </p>
          </div>
        )}

        {tab === "card" && (
          <ShareCardTab cardUrl={cardUrl} conceptId={conceptId} conceptTitle={conceptTitle} />
        )}

        {tab === "inApp" && canSendInApp && (
          <div>
            {!picked ? (
              <>
                <div className="label mb-2">Find a seeker</div>
                <input
                  data-testid="share-user-search"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="name or email…"
                  className="input-line w-full text-sm"
                />
                {results.length > 0 && (
                  <ul data-testid="share-results" className="mt-3 max-h-56 overflow-y-auto hairline">
                    {results.map((u) => (
                      <li key={u.user_id}>
                        <button
                          data-testid={`share-pick-${u.user_id}`}
                          onClick={() => setPicked(u)}
                          className="w-full text-left px-3 py-3 hover:bg-secondary flex items-center gap-3"
                        >
                          {u.picture
                            ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                            : <div className="w-7 h-7 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-sm">{(u.name || u.email)[0]?.toUpperCase()}</div>}
                          <div className="flex-1">
                            <div className="sans text-sm text-foreground">{u.name || u.email}</div>
                            <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">{u.email}</div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                <div className="paper p-3 flex items-center gap-3 mb-4">
                  <div className="w-7 h-7 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-sm">{(picked.name || picked.email)[0]?.toUpperCase()}</div>
                  <div className="flex-1">
                    <div className="sans text-sm text-foreground">{picked.name || picked.email}</div>
                    <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">{picked.email}</div>
                  </div>
                  <button onClick={() => setPicked(null)} className="text-muted-foreground hover:text-foreground" data-testid="share-unpick">
                    <X size={14} />
                  </button>
                </div>
                <textarea
                  data-testid="share-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={280}
                  rows={3}
                  placeholder="Optional: a line of context, like a margin-note…"
                  className="input-line w-full text-sm resize-none"
                />
                <button
                  data-testid="share-send"
                  onClick={send}
                  disabled={sending}
                  className="btn-gold w-full mt-4 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send size={12} />
                  {sending ? "Sending…" : "Send"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function ShareCardTab({ cardUrl, conceptId, conceptTitle }) {
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(cardUrl, { credentials: "include" });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (conceptTitle || conceptId).replace(/[^a-z0-9-_]/gi, "-").slice(0, 60);
      a.href = objectUrl;
      a.download = `correlate-${safeName}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Card downloaded.");
    } catch {
      toast.error("Could not download the card.");
    } finally {
      setDownloading(false);
    }
  };

  const copyCardUrl = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      toast.success("Card image URL copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy.");
    }
  };

  return (
    <div data-testid="share-card-tab">
      <div className="hairline overflow-hidden bg-secondary">
        <img
          src={cardUrl}
          alt={`${conceptTitle} share card`}
          className="block w-full h-auto"
          data-testid="share-card-preview"
        />
      </div>
      <p className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-4">
        A 1200×630 image — perfect for X, LinkedIn, or downloading to share.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid="share-card-download"
          onClick={download}
          disabled={downloading}
          className="btn-gold flex items-center gap-2 disabled:opacity-50"
        >
          <Download size={12} />
          {downloading ? "Preparing…" : "Download PNG"}
        </button>
        <button
          data-testid="share-card-copy-url"
          onClick={copyCardUrl}
          className="reaction-pill"
        >
          {copied ? <Check size={12} /> : <ImageIcon size={12} />}
          {copied ? "Copied" : "Copy image URL"}
        </button>
      </div>
    </div>
  );
}
